import { useEffect, useMemo, useRef, useState } from 'react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { stripHtml } from './utils';

export interface ReadMoreTextProps {
    /** Plain text content (use `html` for rich content instead). */
    text?: string;
    /**
     * Rich-text (HTML) content, used in place of `text` when set. The crop is
     * measured with a Range over a single text node, so this is flattened to
     * plain text rather than rendered — the markup cannot survive a
     * character-offset slice. Callers wanting the formatting intact render the
     * full copy elsewhere and point Read more at it via `anchorHref`.
     */
    html?: string;
    /** Target line count for the collapsed summary (default 3). */
    lines?: number;
    /** Class for the text itself. */
    className?: string;
    /** Class for the read-more/less toggle. */
    toggleClassName?: string;
    /**
     * When set, Read more is a link instead of an in-place expand — the text
     * stays cropped and the full copy lives at the target. An in-page anchor
     * (`#id`) smooth-scrolls to it (e.g. the product-details block); any other
     * href is a client-side route link (e.g. the professional's profile).
     */
    anchorHref?: string;
    /**
     * Replaces the default in-page anchor handling — for targets that need
     * more than a scroll (e.g. opening a collapsed accordion row first). The
     * handler owns preventDefault and the scroll.
     */
    onAnchorClick?: (event: React.MouseEvent) => void;
}

/**
 * Read more keeps the copy's size and weight — it continues the sentence rather
 * than sitting under it as a control — but rests a step down in grey so it
 * still separates from the sentence, and darkens to the copy's own ink on
 * hover. Consumers override the pair via `toggleClassName` per theme.
 */
const READ_MORE_CLASS =
    'cursor-pointer font-[inherit] text-[#8f8f8f] no-underline transition-colors hover:text-[#181818]';

const SENTENCE_PATTERN = /[^.!?]*[.!?]+(?=\s|$)|[^.!?]+$/g;

/**
 * End offsets of every complete sentence, plus the end of the text so copy that
 * never terminates still has a boundary to crop at.
 */
const sentenceEndOffsets = (value: string): number[] => {
    const offsets: number[] = [];
    for (const match of value.matchAll(SENTENCE_PATTERN)) {
        const end = (match.index ?? 0) + match[0].length;
        if (end > 0) offsets.push(end);
    }
    if (!offsets.length || offsets[offsets.length - 1] !== value.length) {
        offsets.push(value.length);
    }
    return offsets;
};

/**
 * Shows the copy cropped to a whole sentence — never mid-word, and never with
 * an ellipsis — choosing whichever sentence ends nearest `lines`. That may land
 * a little under or over; a sentence cut in half to hit an exact line count
 * reads worse than one line too many.
 *
 * Read more follows inline as part of that last sentence, and the whole block
 * carries the link, so the target is the paragraph rather than two small words.
 */
export const ReadMoreText = ({
    text,
    html,
    lines = 3,
    className,
    toggleClassName,
    anchorHref,
    onAnchorClick,
}: ReadMoreTextProps) => {
    const measureRef = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    // null until measured — until then the copy renders cropped by height, so
    // the first paint never flashes the full text.
    const [cropOffset, setCropOffset] = useState<number | null>(null);
    // Whether there is more copy than the space shows. Tracked separately from
    // the crop offset because the two can disagree: copy with no sentence
    // boundary to cut at overflows without the slice shortening it.
    const [overflows, setOverflows] = useState(false);

    const plainText = useMemo(
        () => (html ? stripHtml(html) : (text ?? '')),
        [html, text],
    );

    useEffect(() => {
        const el = measureRef.current;
        if (!el || !plainText) return;

        const measure = () => {
            const node = el.firstChild;
            if (!node) return;
            const range = document.createRange();
            const linesTo = (offset: number) => {
                range.setStart(node, 0);
                range.setEnd(node, offset);
                return range.getClientRects().length;
            };

            const total = linesTo(plainText.length);
            // Everything fits — no crop, and no Read more.
            if (total <= lines) {
                setCropOffset(plainText.length);
                setOverflows(false);
                return;
            }

            let best = plainText.length;
            let bestDistance = Number.POSITIVE_INFINITY;
            for (const offset of sentenceEndOffsets(plainText)) {
                const distance = Math.abs(linesTo(offset) - lines);
                // Strictly-less keeps the earliest boundary on a tie, so a tie
                // resolves to the shorter summary.
                if (distance < bestDistance) {
                    bestDistance = distance;
                    best = offset;
                }
            }
            setCropOffset(best);
            setOverflows(true);
        };

        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [plainText, lines]);

    // There is more to read: what Read more is offered for.
    const isCropped = overflows;
    // Whether the slice itself shortened the copy. Copy that never terminates —
    // no full stop, or a bullet list whose items flatten to a single run — has
    // no boundary before the end, so it overflows with nothing trimmed.
    const sentenceCropped =
        cropOffset !== null && cropOffset < plainText.length;
    const visibleText =
        expanded || cropOffset === null
            ? plainText
            : plainText.slice(0, cropOffset).trimEnd();

    // Bounded by height rather than `-webkit-line-clamp`: the clamp paints an
    // ellipsis, which is the very thing the crop exists to avoid. Needed before
    // the first measurement, and afterwards for copy the slice could not
    // shorten — without it that copy renders in full, unbounded.
    const clampStyle =
        !expanded && (cropOffset === null || (overflows && !sentenceCropped))
            ? ({ maxHeight: `${lines}lh`, overflow: 'hidden' } as const)
            : undefined;

    const handleAnchorClick = (event: React.MouseEvent) => {
        if (!anchorHref?.startsWith('#')) return;
        const target = document.getElementById(anchorHref.slice(1));
        if (!target) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    // With an anchor the whole block is already a link, so the affordance is
    // inert text inside it — a control nested in a link is neither valid nor
    // reachable. Expanding in place has no such wrapper, so there it has to BE
    // the control: a span with a click handler is invisible to the keyboard and
    // announces nothing, which is what this was.
    const toggleLabel = (label: string) =>
        anchorHref ? (
            <span className={cn(READ_MORE_CLASS, toggleClassName)}>
                {label}
            </span>
        ) : (
            <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                className={cn(
                    READ_MORE_CLASS,
                    'bg-transparent p-0',
                    toggleClassName,
                )}
            >
                {label}
            </button>
        );

    const body = (
        <>
            {visibleText}{' '}
            {isCropped && !expanded ? toggleLabel('Read more') : null}
            {isCropped && expanded && !anchorHref
                ? toggleLabel('Read less')
                : null}
        </>
    );

    return (
        // relative so the measuring twin below can span this exact width.
        <div className="relative">
            {/*
                Hidden twin holding the full copy in one text node: the crop is
                measured with a Range over it, which needs the real column width
                and type. `inset-x-0` is load-bearing — an absolute block with
                no width shrink-wraps, and every line break would be measured
                against the wrong column. aria-hidden and out of flow, so it
                neither reads nor takes space.
            */}
            <p
                ref={measureRef}
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 -z-10 m-0 opacity-0 select-none',
                    className,
                )}
            >
                {plainText}
            </p>

            {isCropped && anchorHref ? (
                anchorHref.startsWith('#') ? (
                    <a
                        href={anchorHref}
                        onClick={onAnchorClick ?? handleAnchorClick}
                        className={cn('m-0 block no-underline', className)}
                    >
                        {body}
                    </a>
                ) : (
                    <RoccoLink
                        to={anchorHref}
                        className={cn('m-0 block no-underline', className)}
                    >
                        {body}
                    </RoccoLink>
                )
            ) : isCropped ? (
                <p className={cn('m-0', className)} style={clampStyle}>
                    {body}
                </p>
            ) : (
                <p className={cn('m-0', className)} style={clampStyle}>
                    {visibleText}
                </p>
            )}
        </div>
    );
};
